/**
 * F45 (Wave 2D) — shared funnel renderer: the `Chart kind="funnel"` visual +
 * a per-step Table (count · step-conversion · drop-off · source · PoP delta).
 * Reused by the dashboard booking-funnel summary, the dedicated funnels page,
 * and the scorecard funnel snapshot.
 */
import { useMemo } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import { Badge, Chart, Table, type TableColumn } from '@bravemove/ui-web';
import type { AnalyticsFunnelResult, AnalyticsFunnelStepResult } from '@bravemove/types';
import { PercentDelta } from './PercentDelta';

export interface FunnelPanelProps {
  readonly result: AnalyticsFunnelResult;
  readonly showTable?: boolean;
  readonly testId?: string;
}

function fmtPct(v: number | undefined): string {
  if (v === undefined) return '—';
  return `${Math.round(v * 1000) / 10}%`;
}

export function FunnelPanel({
  result,
  showTable = true,
  testId = 'analytics-funnel-panel',
}: FunnelPanelProps) {
  const { t } = useI18n();

  const chartData = useMemo(
    () =>
      result.steps.map((s) => ({
        stage: t(`admin:analytics.funnelStep.${s.stepKey}`, { defaultValue: s.stepKey }),
        value: s.count,
      })),
    [result.steps, t],
  );

  const columns = useMemo<readonly TableColumn<AnalyticsFunnelStepResult>[]>(
    () => [
      {
        key: 'step',
        header: t('admin:analytics.funnel.step'),
        cell: (row) => (
          <span className="text-[13px] font-semibold text-ink" data-step-key={row.stepKey}>
            {t(`admin:analytics.funnelStep.${row.stepKey}`, { defaultValue: row.stepKey })}
          </span>
        ),
      },
      {
        key: 'source',
        header: t('admin:analytics.funnel.source'),
        cell: (row) => (
          <Badge tone={row.source === 'ga4' ? 'info' : 'neutral'}>
            {row.source === 'ga4'
              ? t('admin:analytics.funnel.sourceGa4')
              : t('admin:analytics.funnel.sourcePostgres')}
          </Badge>
        ),
      },
      {
        key: 'count',
        header: t('admin:analytics.funnel.count'),
        align: 'end',
        cell: (row) => <span className="font-mono tabular-nums">{row.count.toLocaleString()}</span>,
      },
      {
        key: 'conversion',
        header: t('admin:analytics.funnel.stepConversion'),
        align: 'end',
        cell: (row) => (
          <span className="font-mono tabular-nums">{fmtPct(row.conversionFromPrev)}</span>
        ),
      },
      {
        key: 'dropoff',
        header: t('admin:analytics.funnel.dropOff'),
        align: 'end',
        cell: (row) => (
          <span className="font-mono tabular-nums">{fmtPct(row.dropoffFromPrev)}</span>
        ),
      },
      {
        key: 'pop',
        header: t('admin:analytics.vsPrevious'),
        align: 'end',
        cell: (row) =>
          row.deltaPercent === undefined ? (
            <span className="text-ink-3">—</span>
          ) : (
            <PercentDelta deltaPercent={row.deltaPercent} />
          ),
      },
    ],
    [t],
  );

  return (
    <div className="space-y-4" data-testid={testId}>
      <Chart kind="funnel" data={chartData} xKey="stage" series={[{ key: 'value' }]} height={240} />
      {showTable ? (
        <Table
          data={result.steps as AnalyticsFunnelStepResult[]}
          columns={columns as TableColumn<AnalyticsFunnelStepResult>[]}
          rowKey={(row) => row.stepKey}
          density="compact"
        />
      ) : null}
    </div>
  );
}
